import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Camera, Upload, CheckCircle, Loader2, Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import config from '../config';

export const ProjectProgressPage = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({
    stage: 'initial_site_visit',
    description: '',
    percentage: 0,
  });
  const [images, setImages] = useState([]);
  const [stages, setStages] = useState([]);

  const stageOptions = [
    { value: 'initial_site_visit', label: 'بازدید اولیه' },
    { value: 'roof_preparation', label: 'آماده‌سازی سقف' },
    { value: 'panel_installation', label: 'نصب پنل‌ها' },
    { value: 'wiring', label: 'سیم‌کشی' },
    { value: 'inverter_installation', label: 'نصب اینورتر' },
    { value: 'testing', label: 'تست و راه‌اندازی' },
    { value: 'final_handover', label: 'تحویل نهایی' },
  ];

  useEffect(() => {
    fetchStages();
  }, [contractId]);

  const fetchStages = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/marketplace/contracts/${contractId}/progress/`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStages(data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({ file, preview: URL.createObjectURL(file) }));
    setImages([...images, ...newImages]);
  };

  const removeImage = (index) => {
    URL.revokeObjectURL(images[index].preview);
    setImages(images.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      formData.append('stage', progress.stage);
      formData.append('description', progress.description);
      formData.append('percentage', progress.percentage);
      
      images.forEach((img, index) => {
        formData.append(`images`, img.file);
      });
      
      const response = await fetch(config.getApiUrl(`/marketplace/contracts/${contractId}/progress/`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('مرحله با موفقیت ثبت شد');
        setImages([]);
        setProgress({ stage: 'initial_site_visit', description: '', percentage: 0 });
        fetchStages();
      } else {
        toast.error('خطا در ثبت مرحله');
      }
    } catch (error) {
      toast.error('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="bg-blue-600 p-6 text-white">
            <h1 className="text-2xl font-bold">گزارش پیشرفت پروژه</h1>
            <p className="text-blue-100 mt-2">ثبت مراحل انجام کار</p>
          </div>
          
          <div className="p-6">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between text-sm mb-2">
                <span>پیشرفت کلی پروژه</span>
                <span>{progress.percentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-600 h-2 rounded-full" style={{ width: `${progress.percentage}%` }}></div>
              </div>
            </div>
            
            {/* Stages Timeline */}
            <div className="mb-8">
              <h3 className="font-bold mb-4">مراحل انجام شده</h3>
              <div className="space-y-3">
                {stages.map((stage, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <div className="flex-1">
                      <span className="font-medium">{stageOptions.find(s => s.value === stage.stage)?.label || stage.stage}</span>
                      <p className="text-sm text-gray-500">{stage.description}</p>
                    </div>
                    <span className="text-xs text-gray-400">{new Date(stage.created_at).toLocaleDateString('fa-IR')}</span>
                  </div>
                ))}
                {stages.length === 0 && (
                  <p className="text-gray-500 text-center py-4">هنوز مرحله‌ای ثبت نشده است</p>
                )}
              </div>
            </div>
            
            {/* New Stage Form */}
            <form onSubmit={handleSubmit} className="border-t pt-6">
              <h3 className="font-bold mb-4">ثبت مرحله جدید</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">مرحله کار</label>
                  <select
                    value={progress.stage}
                    onChange={(e) => setProgress({...progress, stage: e.target.value})}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  >
                    {stageOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">توضیحات</label>
                  <textarea
                    value={progress.description}
                    onChange={(e) => setProgress({...progress, description: e.target.value})}
                    rows={3}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="شرح فعالیت‌های انجام شده..."
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">درصد پیشرفت کل پروژه</label>
                  <input
                    type="range"
                    value={progress.percentage}
                    onChange={(e) => setProgress({...progress, percentage: parseInt(e.target.value)})}
                    min="0"
                    max="100"
                    className="w-full"
                  />
                  <div className="text-center mt-1">{progress.percentage}%</div>
                </div>
                
                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">تصاویر (اختیاری)</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-500">
                    <label className="cursor-pointer flex flex-col items-center">
                      <Camera className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">برای آپلود تصاویر کلیک کنید</span>
                      <input type="file" multiple accept="image/*" className="hidden" onChange={handleImageUpload} />
                    </label>
                  </div>
                  
                  {images.length > 0 && (
                    <div className="grid grid-cols-4 gap-3 mt-3">
                      {images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img src={img.preview} alt="" className="w-full h-24 object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => navigate('/installer/contracts')}
                  className="px-6 py-2 border rounded-lg"
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  ثبت مرحله
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
